// Import ModuleId from its DECLARING module, not from ModuleContext.
//
// ModuleContext re-declares the same union but also imports modules/registry.ts,
// which imports every module's routes and therefore every page in OfficeHub. A
// type-only import is erased by the bundler, so this was invisible in every
// bundle -- but `tsc` must load the module to read the type, so this one line
// pulled 2,830 extra client/src files into the type closure of any satellite app
// that reaches AuthContext (-> typesenseScopedSearch -> here). Measured: 2,884
// reachable files before, 54 after. On a CI runner that installs only the
// satellite's own deps, those files' bare imports are unresolvable -- 1,543
// TS2307 plus ~6,000 cascading implicit-any errors in shop-client.
//
// client/src/modules/types.ts is the authority for ModuleId (see CLAUDE.md,
// "Module Architecture") and is a leaf: react-router-dom + types/firestore only.
import type { ModuleId } from "../modules/types";

/**
 * Static collection priority per module.
 *
 * Collections listed first are shown under "In this module"; the rest appear
 * under "Other results". Array order is authoritative within each section —
 * a collection listed earlier always renders above one listed later,
 * regardless of BM25 score (see sortByPriorityOrder in useGlobalSearch.ts).
 * Put high-volume, low-signal collections (e.g. call_logs) last so a lucky
 * high-scoring hit doesn't bury more targeted collections.
 */
export const MODULE_COLLECTION_PRIORITY: Partial<
  Record<ModuleId, readonly string[]>
> = {
  P1: ["leads", "contacts", "qbo_customers", "quotes", "call_logs"],
  P2: ["quotes", "price_book_items", "qbo_customers", "contacts"],
  P4: ["qbo_invoices", "qbo_customers"],
  P5: ["support_cases", "contacts", "qbo_customers", "call_logs"],
  P6: ["issue_reports"],
  P7: ["assets", "equipment", "form_submissions"],
  P8: ["form_submissions", "service_tickets"],
  P9: ["employees"],
  P10: ["projects", "equipment", "contacts", "qbo_customers"],
  // Generators ahead of the parts list: a generator record is almost always the
  // answer a Field Service user is looking for (Nick, 2026-09-29).
  P11: [
    "service_tickets",
    "equipment",
    "service_parts_list",
    "form_submissions",
    "fluid_samples",
  ],
  P12: ["service_tickets", "equipment", "service_parts_list"],
  P13: ["employees"],
  P14: ["contacts", "employees"],
  P15: ["leads", "contacts"],
  P16: ["employees"],
  P17: ["employees", "form_submissions", "equipment"],
  P18: ["investors"],
  P19: ["employees"],
  AI_SYSTEMS: ["issue_reports"],
};

/**
 * Collections NOT on the browser's search key (#5550, #6079, BILLING-LOCK): assets (Owner or Fleet P7),
 * investors (Owner), employees (HR admin/coordinator), issue_reports (Admin/Developer/Owner), contacts
 * (candidate records only with recruiting access, #3228) and the QuickBooks finance and customer indexes. Cmd+K searches them through the server
 * callable search_searchRestricted, which checks the caller's role per query. The
 * server's authority is functions/src/search/searchAccess.ts; this list only routes.
 */
export const ROLE_GATED_COLLECTIONS: ReadonlySet<string> = new Set([
  "assets",
  "investors",
  "employees",
  "issue_reports",
  // #3228: a candidate record needs recruiting access, which a key filtered by org cannot express; the
  // server adds `isCandidate:=false` for a caller without it.
  "contacts",
  // BILLING-LOCK (Nick 2026-10-03): their indexes carry money, so the server decides per query
  // (Finance for invoices/bills/vendors, Finance or Quoting for estimates, any employee for
  // customers with the balance withheld unless Finance).
  "qbo_invoices",
  "qbo_bills",
  "qbo_vendors",
  "qbo_estimates",
  "qbo_customers",
  // Quote documents carry costs and margin: management, Quoting (P2) and Field Service (P11) only.
  "quotes",
  // Price book items carry quote cost, margin and price history (#6994): the same people as quotes.
  "price_book_items",
]);

/**
 * Role-gated on the server but NOT offered as Cmd+K results: no page shows one record of these, so a
 * result would land on a dashboard (FIN-D1, L-1260). Kept in ROLE_GATED_COLLECTIONS and server-gated so
 * re-adding one to COLLECTION_META (with a real detail page) is gated from the start. The server guard
 * functions/src/search/__tests__/ensureSearchKey.test.ts requires COLLECTION_META + this set to be
 * exactly the server's searchable + role-gated set, so a collection can only be left out BY NAME here.
 */
export const ROLE_GATED_NOT_OFFERED: ReadonlySet<string> = new Set([
  "qbo_bills",
  "qbo_vendors",
  "qbo_estimates",
]);

/**
 * Score dampening applied to collections with very high result volume
 * relative to per-query signal (e.g. call_logs — every call touches a lot of
 * loosely-matching free text). Applied only when a collection falls into the
 * unprioritized "Other results" bucket (no MODULE_COLLECTION_PRIORITY entry
 * covers it for the current module) so sheer row count can't let it win pure
 * BM25 score sorting and bury smaller, more targeted collections. Explicit
 * high-confidence intent matches (e.g. a phone number query) bypass this —
 * see detectQueryIntent in typesense-service.ts.
 */
export const NOISY_COLLECTION_WEIGHT: Partial<Record<string, number>> = {
  call_logs: 0.5,
};

/**
 * Typesense client configuration.
 *
 * Note: We use raw fetch() in `typesense-service.ts` instead of the
 * typesense-js SDK. The SDK has axios+webpack interop issues that
 * break in the production bundle. The HTTP API is simple enough that
 * a thin fetch wrapper is more reliable.
 */

/** Returns true if Typesense is configured and enabled via env vars */
export function isTypesenseEnabled(): boolean {
  return (
    process.env.REACT_APP_TYPESENSE_ENABLED === "true" &&
    !!process.env.REACT_APP_TYPESENSE_HOST
  );
}

/**
 * The id a search hit is opened by: the first of `idFields` that is filled in, else the index's own `id`, else "".
 * A hit whose id field is missing must still produce a URL the detail page can resolve, never "".
 */
export function hitDocId(idFields: readonly string[], doc: Record<string, unknown>): string {
  for (const field of idFields) {
    const v = doc[field];
    if (typeof v === "string" && v.trim()) return v.trim();
    if (typeof v === "number") return String(v);
  }
  const fallback = doc.id;
  return typeof fallback === "string" ? fallback.trim() : "";
}

/** Collection display metadata for search result rendering.
 *  moduleId maps to ModuleRegistry — used to filter results by user access. */
export const COLLECTION_META: Record<
  string,
  {
    label: string;
    icon: string;
    route: (id: string) => string;
    idField: string;
    /** Tried in order when `idField` is blank on a hit, before the index's own `id`. */
    idFallbackFields?: readonly string[];
    moduleId: ModuleId;
  }
> = {
  call_logs: {
    label: "Calls",
    icon: "Phone",
    route: (id) => `/support/inbox/calls/${id}`,
    idField: "callLogId",
    moduleId: "P5",
  },
  contacts: {
    label: "Contacts",
    icon: "Users",
    route: (id) => `/crm/contacts/${id}`,
    idField: "contactId",
    moduleId: "P1",
  },
  projects: {
    label: "Projects",
    icon: "FolderKanban",
    route: (id) => `/projects/${id}`,
    idField: "projectId",
    moduleId: "P10",
  },
  leads: {
    label: "Leads",
    icon: "Target",
    route: (id) => `/crm/leads/${id}`,
    idField: "leadId",
    moduleId: "P1",
  },
  equipment: {
    label: "Generators",
    icon: "Zap",
    route: (id) => `/field-service/generators/${id}`,
    idField: "equipmentId",
    moduleId: "P11",
  },
  support_cases: {
    label: "Cases",
    icon: "LifeBuoy",
    route: (id) => `/support/cases/${id}`,
    idField: "caseId",
    moduleId: "P5",
  },
  service_tickets: {
    label: "Service Logs",
    icon: "Wrench",
    route: (id) => `/field-service/tickets/${id}`,
    idField: "appSheetServiceId",
    moduleId: "P11",
  },
  fluid_samples: {
    label: "Fluid Samples",
    icon: "FlaskConical",
    // The workspace's ?sample= deep link opens the sample view (useSampleDeepLink).
    route: (id) => `/field-service/samples?sample=${encodeURIComponent(id)}`,
    idField: "fluidSampleId",
    moduleId: "P11",
  },
  quotes: {
    label: "Quotes",
    icon: "FileText",
    // /quoting/:id matched only the Quoting catch-all and opened the dashboard; the quote page is quotes/:quoteId.
    route: (id) => `/quoting/quotes/${id}`,
    idField: "quoteId",
    moduleId: "P2",
  },
  employees: {
    label: "Employees",
    icon: "UserCircle",
    route: (id) => `/hr/employees/${id}`,
    idField: "id",
    moduleId: "P9",
  },
  assets: {
    label: "Assets",
    icon: "Truck",
    route: (id) => `/assets/${id}`,
    idField: "assetId",
    moduleId: "P7",
  },
  form_submissions: {
    label: "Forms",
    icon: "ClipboardCheck",
    route: (id) => `/forms/submissions/${id}`,
    idField: "id",
    moduleId: "P8",
  },
  price_book_items: {
    label: "Price Book",
    icon: "BookOpen",
    route: (id) => `/quoting/price-book?item=${id}`,
    idField: "itemId",
    moduleId: "P2",
  },
  service_parts_list: {
    label: "Parts Catalog",
    icon: "Package",
    // Cmd+K "Parts Catalog" opened a BLANK page: there is no /field-service/parts/:id route (the page lives at
    // parts-catalog/:partId), and no mirror doc has a `partsListId` field. The page accepts the mirror doc id
    // or the AppSheet Part ID, so the Part ID is the id.
    // Encoded: ~9% of part numbers (the fallback id) contain "/" or spaces, e.g. 20RCA-3/208.
    route: (id) => `/field-service/parts-catalog/${encodeURIComponent(id)}`,
    idField: "appSheetPartId",
    // The index's own id (what a hit falls back to) is no record id; the part number is resolved when it is unique.
    idFallbackFields: ["partNumber"],
    moduleId: "P11",
  },
  qbo_customers: {
    label: "QBO Customers",
    icon: "Users",
    route: (id) => `/billing/customers/${id}`,
    idField: "id",
    moduleId: "P4",
  },
  // QuickBooks bills, vendors and estimates are NOT offered as results: no page shows one record
  // (/billing/bills/:id, /billing/vendors/:id and /quoting/estimates/:id never existed, so a click landed
  // silently on the module dashboard). Add one back only with a real detail page; see
  // __tests__/typesense-route-resolves.test.ts. Global search queries only the collections listed here, so
  // the browser never searches them now; their ROLE_GATED_COLLECTIONS entries and typesense-service.ts
  // query weights are kept so re-adding one is server-gated and tuned from the start.
  qbo_invoices: {
    label: "QBO Invoices",
    icon: "FileText",
    route: (id) => `/billing/invoicing/${id}`,
    idField: "id",
    moduleId: "P4",
  },
  issue_reports: {
    label: "Issue Reports",
    icon: "Bug",
    route: (id) => `/admin/issue-triage?id=${id}`,
    idField: "issueId",
    moduleId: "P6",
  },
  investors: {
    label: "Investors",
    icon: "Landmark",
    // admin/investors/:id has no route (blank page); one investor's page is admin/cap-table/:investorId.
    route: (id) => `/investors/admin/cap-table/${id}`,
    idField: "investorId",
    moduleId: "P18",
  },
};

/**
 * Per-collection projection for the global (Cmd+K) search — the exact document
 * fields that path renders, sent to Typesense as `include_fields`.
 *
 * Without it Typesense returns the WHOLE document for every hit, and the palette
 * asks ~20 collections for 5 hits each on every debounced keystroke. It renders
 * roughly six fields per row (see CommandPalette's getTitle / getSubtitle), so
 * everything else on those ~100 documents is transferred and JSON-parsed on the
 * main thread purely to be thrown away.
 *
 * This is a DECLARED fact table, not something inferred from the renderer at
 * runtime — the same reasoning as hosting-deps.json. A projection that silently
 * omits a field the renderer needs would blank a result row rather than fail
 * loudly, so the guarantee comes from a test (typesense-include-fields.test.ts)
 * that reads the field accesses out of CommandPalette's switch statements and
 * asserts every one is covered here. That test also asserts it actually found
 * accesses for each collection, so a parser that quietly matches nothing fails
 * instead of passing.
 *
 * Each entry must contain: every `doc.<field>` read by getTitle and getSubtitle
 * for that collection, the collection's `idField` (used to build the route), and
 * `isCandidate` for `contacts` (drives the Candidate badge and its route).
 *
 * A collection absent from this table is sent with NO projection — full
 * documents, i.e. today's behavior. That fail-open is deliberate: a newly added
 * collection renders correctly but unoptimized, rather than rendering blank.
 */
export const COLLECTION_INCLUDE_FIELDS: Record<string, string> = {
  call_logs: "callLogId,callerName,callerPhone,branch,status,createdAt",
  contacts: "contactId,fullName,companyName,email,phone,isCandidate",
  projects: "projectId,clientName,installId,status,address,updatedAt",
  leads: "leadId,companyName,contactName,opportunityName,phone,address",
  equipment:
    "equipmentId,customerName,generatorMake,generatorModel,generatorSerialNumber,address,status",
  support_cases: "caseId,caseNumber,subject,status,priority,requesterName,createdAt",
  service_tickets:
    "appSheetServiceId,customerName,status,technician,address,createdAt,generatorId",
  quotes: "quoteId,quoteNumber,subject,status,customerName",
  fluid_samples: "fluidSampleId,sampleId,fluidType,unitName,customerName,serviceTicketId,status",
  employees: "id,fullName,title,department,status",
  assets: "assetId,name,status,assignedToName,vin",
  form_submissions: "id,templateName,status,customerName,submittedByName",
  price_book_items: "itemId,partNumber,description,category,supplier,manufacturer",
  service_parts_list:
    "appSheetPartId,partNumber,partDescription,branch,category,binLocation",
  qbo_customers:
    "id,fullyQualifiedName,displayName,companyName,balance,email,phone",
  qbo_invoices: "id,docNumber,customerName,totalAmt,balance,eInvoiceStatus,txnDate",
  issue_reports:
    "issueId,issueCode,title,status,severity,classification,reportedByName",
  investors: "investorId,name,entityName,email,status",
};
